import { FetchResult, gql } from '@apollo/client';
import { Button, Divider, Elevation, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Formik } from 'formik';
import { useSubmit } from 'formik-apollo';
import React from 'react';
import * as Yup from 'yup';
import { ContentCard, ContentCardFooter, ContentCardHeader, ContentCardScroll } from '../../../components/ContentCard';
import FormGroup from '../../../components/FormGroup';
import PersonOrganisationSelect from '../../../components/PersonOrganisationSelect';
import PersonTagMultiSelect from '../../../components/PersonTagMultiSelect';
import SelectInput from '../../../components/SelectInput';
import Text from '../../../components/Text';
import TextAreaInput from '../../../components/TextAreaInput';
import TextInput from '../../../components/TextInput';
import ToggleInput from '../../../components/ToggleInput';
import {
  CreateSoulInput,
  CreateSoulMutation,
  GdprConsentSource,
  SoulCreateFormFragment,
  SoulEditFormFragment,
  UpdateSoulInput,
  UpdateSoulMutation,
  useCreateSoulMutation,
  useUpdateSoulMutation,
} from '../../../generated/graphql';
import { SOUL_TARGET_GROUP_OPTIONS } from '../../../helpers/constants';
import { convertGermanToIntl } from '../../../helpers/dateTimeUtils';
import { getLaravelValidationErrors, showFormErrorMessage } from '../../../helpers/graphql';
import { buildDiff } from '../../../helpers/utils';

const validationSchema = Yup.object({
  first_name: Yup.string().required('Erforderlich'),
  last_name: Yup.string().required('Erforderlich'),
  birthdate: Yup.string()
    .nullable()
    .matches(/^(3[01]|[12][0-9]|0[1-9])\.(1[012]|0[1-9])\.((?:19|20)\d{2})$/, 'Kein gültiges Datum'),
  email: Yup.string().email('Keine gültige Email').nullable(),
});

type SoulFormProps = {
  onCancel?: () => void;
  onCreated?: (data: CreateSoulMutation) => void;
  onUpdated?: (data: UpdateSoulMutation) => void;
  initialValues: SoulCreateFormFragment | SoulEditFormFragment;
};

const isEditData = (
  initialValues: SoulCreateFormFragment | SoulEditFormFragment,
): initialValues is SoulEditFormFragment => 'id' in initialValues;

const isUpdateResult = (data: CreateSoulMutation | UpdateSoulMutation): data is UpdateSoulMutation =>
  'updateSoul' in data;

const buildUpdateInput = (initialValues: SoulEditFormFragment, values: SoulEditFormFragment): UpdateSoulInput => {
  const { tags, organisation, ...otherValues }: SoulEditFormFragment = buildDiff(initialValues, values);

  return {
    ...otherValues,
    ...(otherValues.birthdate && { birthdate: convertGermanToIntl(otherValues.birthdate) }),
    ...(typeof otherValues.gdpr_consent !== 'undefined' && { gdpr_consent_source: GdprConsentSource.Employee }),
    ...(!!tags && {
      tags: {
        sync: tags.map((tag) => tag.id),
      },
    }),
    ...(typeof organisation !== 'undefined' && {
      organisation: {
        connect: organisation?.id,
        disconnect: !organisation,
      },
    }),
  };
};

const buildCreateInput = (values: SoulCreateFormFragment): CreateSoulInput => ({
  ...values,
  ...(values.birthdate && { birthdate: convertGermanToIntl(values.birthdate) }),
  gdpr_consent_source: GdprConsentSource.Employee,
  tags: {
    sync: values.tags.map((tag) => tag.id),
  },
  organisation: {
    connect: values.organisation?.id,
  },
});

const SoulForm = ({ onCancel, onUpdated, onCreated, initialValues }: SoulFormProps) => {
  const isEdit = isEditData(initialValues);
  const [updateSoul] = useUpdateSoulMutation();
  const [createSoul] = useCreateSoulMutation();
  const handleSubmit = useSubmit<
    SoulCreateFormFragment | SoulEditFormFragment,
    FetchResult<CreateSoulMutation> | FetchResult<UpdateSoulMutation>
  >({
    mutate: (values) =>
      isEditData(values)
        ? updateSoul({
            variables: {
              input: buildUpdateInput(initialValues as SoulEditFormFragment, values),
            },
          })
        : createSoul({
            variables: {
              input: buildCreateInput(values),
            },
          }),
    onCompleted: (res) => {
      if (!res.data) return;
      return isUpdateResult(res.data) ? onUpdated?.(res.data) : onCreated?.(res.data);
    },
    onError: showFormErrorMessage,
    getErrors: getLaravelValidationErrors,
  });

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ isSubmitting, submitForm, dirty }) => (
        <ContentCard elevation={Elevation.FOUR}>
          <ContentCardHeader
            leftElement={<Text large>Genießer {isEdit ? 'bearbeiten' : 'hinzufügen'}</Text>}
            rightElement={<Button onClick={onCancel} icon={IconNames.CROSS} minimal />}
          />

          <ContentCardScroll>
            <FormGroup label="Vorname" labelInfo="(erforderlich)" name="first_name">
              <TextInput name="first_name" placeholder="Vorname" />
            </FormGroup>
            <FormGroup label="Nachname" labelInfo="(erforderlich)" name="last_name">
              <TextInput name="last_name" placeholder="Nachname" />
            </FormGroup>
            <FormGroup label="Email" name="email">
              <TextInput name="email" type="email" placeholder="Email" />
            </FormGroup>
            <FormGroup label="Geburtsdatum" name="birthdate">
              <TextInput name="birthdate" placeholder="TT.MM.JJJJ" />
            </FormGroup>
            <FormGroup
              label="Datenschutz"
              name="gdpr_consent"
              helperText="Genießer erhält keine Newsletter, wenn keine ausdrückliche Einwilligung vorliegt"
            >
              <ToggleInput name="gdpr_consent" label="Newsletter-Einwilligung erteilt" />
            </FormGroup>
            <FormGroup label="Telefon" name="phone">
              <TextInput name="phone" type="text" placeholder="Telefon" />
            </FormGroup>
            <FormGroup label="Mobil" name="mobile">
              <TextInput name="mobile" type="text" placeholder="Mobil" />
            </FormGroup>
            <FormGroup label="Adresse" name="street">
              <TextInput name="street" type="text" placeholder="Straße" />
            </FormGroup>
            <FormGroup name="postal_code">
              <TextInput name="postal_code" type="text" placeholder="PLZ" />
            </FormGroup>
            <FormGroup name="city">
              <TextInput name="city" type="text" placeholder="Ort" />
            </FormGroup>

            <Divider className="my-6 -mx-5" />
            <FormGroup label="Zielgruppe" name="meta.target_group">
              <SelectInput name="meta.target_group" options={SOUL_TARGET_GROUP_OPTIONS} />
            </FormGroup>
            <FormGroup label="Besonderheiten" name="meta">
              <ToggleInput name="meta.buys_ticket" label="Selbstzahler" />
              <ToggleInput name="meta.needs_blind" label="Ist Blind" />
              <ToggleInput name="meta.needs_wheelchair" label="Benötigt Rollstuhlplatz" />
              <ToggleInput name="meta.needs_wheeled_walker" label="Nutzt Rollator" />
              <ToggleInput name="meta.needs_drive" label="Muss von Zuhause abgeholt werden" />
              <ToggleInput name="meta.needs_trainstation_fetch" label="Muss vom Bahnhof abgeholt werden" />
            </FormGroup>
            <FormGroup label="Attribute" name="tags">
              <PersonTagMultiSelect name="tags" />
            </FormGroup>
            <FormGroup label="Organisation" name="organisation">
              <PersonOrganisationSelect name="organisation" />
            </FormGroup>
            <FormGroup label="Interne Notizen" name="notes">
              <TextAreaInput name="notes" placeholder="Interne Notizen" fill rows={10} />
            </FormGroup>
          </ContentCardScroll>

          <ContentCardFooter
            rightElement={
              <>
                <Button text="Abbrechen" onClick={onCancel} className="ml-2" />
                <Button
                  text={isEdit ? 'Änderungen Sichern' : 'Hinzufügen'}
                  loading={isSubmitting}
                  disabled={!dirty}
                  intent={Intent.PRIMARY}
                  onClick={submitForm}
                  className="ml-2"
                />
              </>
            }
          />
        </ContentCard>
      )}
    </Formik>
  );
};

SoulForm.fragments = {
  // This is only used for Formik initialValues
  create: gql`
    fragment SoulCreateForm on Soul {
      first_name
      last_name
      status
      birthdate
      email
      gdpr_consent
      phone
      mobile
      street
      city
      postal_code
      notes
      meta {
        target_group
        buys_ticket
        needs_wheelchair
        needs_wheeled_walker
        needs_drive
        needs_trainstation_fetch
        needs_blind
      }
      organisation {
        id
        name
      }
      tags {
        id
        name
        icon
        intent
      }
    }
  `,
  edit: gql`
    fragment SoulEditForm on Soul {
      id
      first_name
      last_name
      birthdate
      email
      gdpr_consent
      phone
      mobile
      street
      city
      postal_code
      notes
      meta {
        target_group
        buys_ticket
        needs_wheelchair
        needs_wheeled_walker
        needs_drive
        needs_trainstation_fetch
        needs_blind
      }
      organisation {
        id
        name
      }
      tags {
        id
        name
        icon
        intent
      }
    }
  `,
};

export default SoulForm;
